import React from "react";

import type { Connection, EnterpriseConnection } from "@auth0/auth0-acul-js";

import { getIcon } from "./iconUtils";

// Google Logo SVG
const GoogleLogo = (): React.ReactElement =>
  React.createElement(
    "svg",
    {
      width: "20",
      height: "20",
      viewBox: "0 0 24 24",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
    },
    React.createElement("path", {
      d: "M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z",
      fill: "#4285F4",
    }),
    React.createElement("path", {
      d: "M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z",
      fill: "#34A853",
    }),
    React.createElement("path", {
      d: "M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z",
      fill: "#FBBC05",
    }),
    React.createElement("path", {
      d: "M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z",
      fill: "#EA4335",
    })
  );

// Apple Logo SVG
const AppleLogo = (): React.ReactElement =>
  React.createElement(
    "svg",
    {
      width: "20",
      height: "20",
      viewBox: "0 0 24 24",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
    },
    React.createElement("path", {
      d: "M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z",
      fill: "currentColor",
    })
  );

export type SocialConnection = Connection | EnterpriseConnection;

interface SocialProviderDetails {
  displayName: string;
  iconComponent: React.ReactNode | null;
}

/**
 * Generates a display name for a social connection.
 * Prefers a direct map from connection.name, then falls back to capitalizing it.
 * If name is unavailable, it attempts to use the strategy.
 */
const generateDisplayName = (connection: SocialConnection): string => {
  // Check if it's an EnterpriseConnection with options.displayName
  if ("options" in connection && connection.options?.displayName) {
    return connection.options.displayName;
  }

  // Fallback to capitalizing the connection.name if not in map
  if (connection.name) {
    return connection.name
      .split(/[-_]/)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  }

  // Last resort: use strategy if name is totally missing
  if (connection.strategy) {
    return (
      connection.strategy.charAt(0).toUpperCase() + connection.strategy.slice(1)
    );
  }

  return "Login Provider";
};

/**
 * Returns an img element for an enterprise connection's configured icon URL.
 */
const getEnterpriseIcon = (
  connection: SocialConnection
): React.ReactNode | null => {
  if ("options" in connection && connection.options?.iconUrl) {
    return React.createElement("img", {
      src: connection.options.iconUrl,
      alt: "",
      width: 20,
      height: 20,
      style: { objectFit: "contain" },
    });
  }
  return null;
};

/**
 * Gets the appropriate SVG logo based on connection name/strategy
 */
const getSocialProviderIcon = (
  connection: SocialConnection
): React.ReactNode => {
  const connectionName = connection.name?.toLowerCase() || "";
  const strategy = connection.strategy?.toLowerCase() || "";

  // Check for enterprise connection icon URL first
  const enterpriseIcon = getEnterpriseIcon(connection);
  if (enterpriseIcon) {
    return enterpriseIcon;
  }

  // Check for Google
  if (connectionName.includes("google") || strategy.includes("google")) {
    return GoogleLogo();
  }

  // Check for Apple
  if (connectionName.includes("apple") || strategy.includes("apple")) {
    return AppleLogo();
  }

  // Fallback to default icon
  return getIcon();
};

/**
 * Gets the display details (displayName, iconComponent) for a social connection.
 */
export const getSocialProviderDetails = (
  connection: SocialConnection
): SocialProviderDetails => {
  const displayName = generateDisplayName(connection);
  const iconComponent = getSocialProviderIcon(connection);

  return {
    displayName,
    iconComponent,
  };
};
